/*
  Product reviews from Yotpo, drawn into our own cards.

  The band only renders when Yotpo has written a review count onto the
  product, so this script starts from a page that expects reviews. It reads
  them from Yotpo's public widget API (the same storefront key the live
  widget uses), drops exact duplicates, and fills the scrolling row from the
  <template> in the section. If Yotpo comes back with nothing, or cannot be
  reached, the whole section is hidden rather than left as an empty band.

  Review text is always set with textContent, never as HTML.
*/
(function () {
  const API = 'https://api-cdn.yotpo.com/v1/widget';

  function hideSection(root) {
    const section = root.closest('.shopify-section') || root;
    section.hidden = true;
  }

  function formatDate(iso) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }

  function dedupe(reviews) {
    const seen = new Set();
    return reviews.filter((r) => {
      const key = [
        (r.content || '').trim().toLowerCase(),
        (r.title || '').trim().toLowerCase(),
        ((r.user && r.user.display_name) || '').trim().toLowerCase(),
        (r.created_at || '').slice(0, 10),
      ].join('|');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function buildCard(template, review, verifiedLabel) {
    const card = template.content.firstElementChild.cloneNode(true);
    const score = Math.max(0, Math.min(5, Math.round(review.score || 0)));

    const stars = card.querySelector('[data-review-stars]');
    stars.setAttribute('aria-label', `${score} out of 5 stars`);
    stars.querySelectorAll('.product-reviews__star').forEach((star, i) => {
      star.classList.toggle('product-reviews__star--empty', i >= score);
    });

    // Only the review itself is shown; Yotpo's separate title is left out.
    // A review with no body text falls back to its title.
    const content = (review.content || '').trim();
    card.querySelector('[data-review-body]').textContent = content || (review.title || '').trim();

    const meta = card.querySelector('[data-review-meta]');
    const items = [];
    if (review.verified_buyer && verifiedLabel) items.push(verifiedLabel);
    const name = review.user && review.user.display_name;
    if (name) items.push(name);
    const date = formatDate(review.created_at);
    if (date) items.push(date);
    items.forEach((text) => {
      const span = document.createElement('span');
      span.className = 'meta-line__item';
      span.textContent = text;
      meta.appendChild(span);
    });
    if (!items.length) meta.remove();

    return card;
  }

  async function load(root) {
    if (root.dataset.smReviewsReady) return;
    root.dataset.smReviewsReady = '';

    const track = root.querySelector('[data-reviews-track]');
    const template = root.querySelector('[data-review-template]');
    const { appKey, productId } = root.dataset;
    if (!track || !template || !appKey || !productId) return hideSection(root);

    const max = parseInt(root.dataset.max, 10) || 20;
    const url = `${API}/${encodeURIComponent(appKey)}/products/${encodeURIComponent(productId)}/reviews.json?per_page=${Math.min(max * 2, 100)}&page=1&sort=date&direction=desc`;

    let reviews = [];
    try {
      const res = await fetch(url, { credentials: 'omit' });
      if (!res.ok) throw new Error(res.status);
      const data = await res.json();
      reviews = (data && data.response && data.response.reviews) || [];
    } catch (e) {
      return hideSection(root);
    }

    reviews = dedupe(reviews).filter((r) => (r.content || r.title)).slice(0, max);
    if (!reviews.length) return hideSection(root);

    const fragment = document.createDocumentFragment();
    reviews.forEach((r) => fragment.appendChild(buildCard(template, r, root.dataset.verifiedLabel)));
    track.querySelectorAll('.sm-skel-card').forEach((el) => el.remove());
    track.appendChild(fragment);

    // The count Yotpo writes onto the product can lag behind, and can include
    // duplicates; once every review is in hand, say how many are actually shown.
    const count = root.querySelector('[data-reviews-count]');
    if (count && reviews.length < max) {
      const label = reviews.length === 1 ? root.dataset.labelOne : root.dataset.labelMany;
      count.textContent = `${reviews.length} ${label || ''}`.trim();
    }

    root.classList.add('is-loaded');
    if (window.smDragScroll && window.smDragScroll.attach) window.smDragScroll.attach(track);
    wireArrows(root, track);
  }

  // Previous / next arrows under the row. They only show when the cards
  // overflow, and each end's arrow is disabled once the row is there.
  function wireArrows(root, track) {
    const nav = root.querySelector('[data-reviews-nav]');
    const prev = root.querySelector('[data-reviews-prev]');
    const next = root.querySelector('[data-reviews-next]');
    if (!nav || !prev || !next) return;

    const step = () => {
      const card = track.firstElementChild;
      if (!card) return track.clientWidth;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const one = card.getBoundingClientRect().width + gap;
      // Move a whole screenful of cards at a time, never less than one card.
      return Math.max(one, Math.floor((track.clientWidth + gap) / one) * one);
    };

    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      nav.hidden = max <= 1;
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= max - 2;
    };

    prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    track.addEventListener('scroll', update, { passive: true });
    new ResizeObserver(update).observe(track);
    update();
  }

  function init() {
    document.querySelectorAll('[data-sm-reviews]').forEach(load);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
